import React, { useEffect, useMemo, useState } from "react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";

/**
 * Every table in a chat answer shows 25 rows at a time: an answer's table, a
 * chart's Table view, a records table. Rendering a 210-row hourly series or
 * 50 runs at once made long threads slow to open and scroll, and each kind
 * paged differently, or not at all.
 */
export const CHAT_PAGE_SIZE = 25;

/** The block title every chat block uses: one size, weight and colour. */
export const BLOCK_TITLE_STYLE = { fontSize: 12, fontWeight: 600, color: theme.textSecondary, lineHeight: 1.4 };

/** rows → the current page of them, and the pager's state. Resets when the rows change. */
export function usePaged(rows, size = CHAT_PAGE_SIZE) {
  const all = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(all.length / size));
  const current = Math.min(page, pages - 1);
  useEffect(() => { setPage(0); }, [all]);
  return {
    visible: all.slice(current * size, current * size + size),
    page: current, pages, size, total: all.length, setPage,
  };
}

const btn = (disabled) => ({
  background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, padding: "2px 8px",
  cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, color: "inherit",
});

/** "1–25 of 210 · Prev 1/9 Next". `note` follows the range (e.g. "· 5,000 in total"). Hidden for one short page with no note. */
export default function ChatPager({ paged, note = "", className = "" }) {
  const { page, pages, size, total, setPage } = paged;
  if (pages <= 1 && !note) return null;
  return (
    <div
      className={`trodo-answer-pager ${className}`}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 2px 0", fontSize: 12, color: theme.textSecondary }}
    >
      <span>
        {total ? `${page * size + 1}–${Math.min(total, (page + 1) * size)} of ${total}` : "0 rows"}
        {note}
      </span>
      {pages > 1 && (
        <span style={{ display: "inline-flex", gap: 6 }}>
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page" style={btn(page === 0)}>Prev</button>
          <span style={{ alignSelf: "center" }}>{page + 1} / {pages}</span>
          <button type="button" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page" style={btn(page >= pages - 1)}>Next</button>
        </span>
      )}
    </div>
  );
}
